import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Slide from '@mui/material/Slide';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LEGAL_NOTICE_DRAWER_ID,
  LEGAL_NOTICE_EMAIL,
  LEGAL_NOTICE_TITLE_ID,
} from './DealTroubleshootingLegalNotice.constants.ts';

type DealTroubleshootingLegalNoticeDrawerProps = {
  open: boolean;
  onClose: () => void;
};

const LEGAL_NOTICE_TEXT = {
  fontSize: 15,
  lineHeight: '22px',
} as const;

export const DealTroubleshootingLegalNoticeDrawer = ({ open, onClose }: DealTroubleshootingLegalNoticeDrawerProps) => {
  const { t } = useTranslation();
  const [overlayElement, setOverlayElement] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  return (
    <Fade in={open} mountOnEnter unmountOnExit timeout={{ enter: 225, exit: 195 }}>
      <Box
        ref={setOverlayElement}
        role="presentation"
        onClick={onClose}
        sx={{
          backgroundColor: theme => alpha(theme.palette.common.black, 0.32),
          bottom: 0,
          display: 'flex',
          left: 0,
          overflow: 'hidden',
          position: 'absolute',
          right: 0,
          top: 0,
          zIndex: theme => theme.zIndex.drawer,
        }}
      >
        <Slide
          container={overlayElement}
          direction="right"
          in={open}
          mountOnEnter
          unmountOnExit
          timeout={{ enter: 225, exit: 195 }}
        >
          <Box
            id={LEGAL_NOTICE_DRAWER_ID}
            role="dialog"
            aria-modal="true"
            aria-labelledby={LEGAL_NOTICE_TITLE_ID}
            tabIndex={-1}
            onClick={event => event.stopPropagation()}
            sx={{
              backgroundColor: theme => theme.palette.background.paper,
              borderRight: theme => `1px solid ${theme.palette.divider}`,
              boxShadow: theme => `0px 2px 4px ${alpha(theme.palette.common.black, 0.08)}`,
              boxSizing: 'border-box',
              flexShrink: 0,
              height: '100%',
              maxWidth: 'min(400px, calc(100% - 32px))',
              minWidth: 0,
              outline: 0,
              overflow: 'auto',
              width: 400,
            }}
          >
            <Stack
              direction="row"
              sx={{
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: theme => theme.palette.background.paper,
                borderBottom: theme => `1px solid ${theme.palette.divider}`,
                flexShrink: 0,
                gap: 1,
                height: 56,
                px: { xs: 2, sm: 3 },
                position: 'sticky',
                top: 0,
                zIndex: 1,
              }}
            >
              <Typography id={LEGAL_NOTICE_TITLE_ID} variant="h3" component="h2" sx={{ minWidth: 0 }}>
                {t('Legal Notice')}
              </Typography>
              <IconButton aria-label={t('Close legal notice')} onClick={onClose} sx={{ flexShrink: 0, p: 0 }}>
                <CloseIcon sx={{ color: 'text.secondary' }} />
              </IconButton>
            </Stack>

            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography component="p" sx={{ ...LEGAL_NOTICE_TEXT, mb: '22px' }}>
                {t(
                  'This Assistant is provided for general troubleshooting support only. Its responses may be incomplete, inaccurate, or mistaken and do not constitute legal advice, compliance advice, or an official OpenX position.'
                )}
              </Typography>

              <Typography component="p" sx={{ ...LEGAL_NOTICE_TEXT, mb: '22px' }}>
                {t(
                  'Recommendations generated by the Assistant may touch on areas subject to legal, regulatory, or policy restrictions, including targeting, domains, creative limitations, and audience settings.'
                )}
              </Typography>

              <Typography component="p" sx={{ ...LEGAL_NOTICE_TEXT, mb: '22px' }}>
                {t(
                  'Certain categories of targeting and advertising, including diversity, demographic, health, political, and other sensitive categories, may be prohibited or restricted depending on the sector, jurisdiction, and context, particularly in the credit, insurance, employment, and housing sectors, and may require additional legal review and safeguards. OpenX also maintains strict requirements relating to under-13 traffic, teen data, and child-directed properties, including traffic restrictions and processing limitations.'
                )}
              </Typography>

              <Typography component="p" sx={LEGAL_NOTICE_TEXT}>
                {t(
                  "You should not rely on the Assistant's recommendations without applying independent judgment and, where appropriate given the limitations described above, consulting your account manager or contacting the OpenX Legal team at"
                )}{' '}
                <Link href={`mailto:${LEGAL_NOTICE_EMAIL}`}>{LEGAL_NOTICE_EMAIL}</Link>.
              </Typography>
            </Box>
          </Box>
        </Slide>
      </Box>
    </Fade>
  );
};
